import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Plus, EditPencil, Trash, Check, Xmark, OpenNewWindow, Search } from "iconoir-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import {
    loadActiveHousehold,
    fetchRecipes,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    rateRecipe,
    clearRecipeRating,
} from "@/lib/api";

export default function RecipesPage() {
    const { user, loading } = useAuth();
    const { t } = useT();
    const nav = useNavigate();
    const active = loadActiveHousehold();
    const [recipes, setRecipes] = useState([]);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    const [editing, setEditing] = useState(null); // recipe id or "new"
    const [draft, setDraft] = useState({ name: "", url: "", notes: "", servings: 4, ingredients: [] });

    useEffect(() => {
        if (!loading && !user) nav("/login", { replace: true });
        if (!active?.household_id) nav("/picker", { replace: true });
    }, [user, loading, active, nav]);

    const reload = async () => {
        if (!active?.household_id) return;
        setBusy(true);
        try {
            const list = await fetchRecipes(active.household_id);
            setRecipes(list || []);
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.household_id]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return recipes;
        return recipes.filter((r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.notes || "").toLowerCase().includes(q) ||
            (r.url || "").toLowerCase().includes(q)
        );
    }, [recipes, query]);

    const startNew = () => {
        setEditing("new");
        setDraft({ name: "", url: "", notes: "", servings: 4, ingredients: [], steps: [] });
    };
    const startEdit = (r) => {
        setEditing(r.id);
        setDraft({
            name: r.name,
            url: r.url || "",
            notes: r.notes || "",
            servings: r.servings || 4,
            ingredients: (r.ingredients || []).map((ing) => ({ ...ing })),
            steps: (r.steps || []).slice(),
        });
    };
    const cancelEdit = () => setEditing(null);

    const save = async () => {
        const name = draft.name.trim();
        if (!name) return;
        // Clean ingredients: drop empty rows, coerce qty
        const cleanIngredients = (draft.ingredients || [])
            .map((ing) => ({
                name: (ing.name || "").trim(),
                qty: ing.qty === "" || ing.qty == null ? null : Number(ing.qty),
                unit: (ing.unit || "").trim() || null,
                category: (ing.category || "").trim() || null,
                note: (ing.note || "").trim() || null,
            }))
            .filter((ing) => ing.name);
        const payload = {
            name,
            url: draft.url.trim() || null,
            notes: draft.notes.trim() || null,
            servings: Number(draft.servings) || 4,
            ingredients: cleanIngredients,
            steps: (draft.steps || []).map((s) => (s || "").trim()).filter(Boolean),
        };
        try {
            if (editing === "new") {
                const created = await createRecipe(active.household_id, payload);
                setRecipes((list) => {
                    const exists = list.some((r) => r.id === created.id);
                    return exists
                        ? list.map((r) => (r.id === created.id ? created : r))
                        : [created, ...list];
                });
            } else {
                const updated = await updateRecipe(editing, payload);
                setRecipes((list) => list.map((r) => (r.id === updated.id ? updated : r)));
            }
            toast.success(t("recipes.saved"));
            setEditing(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (r) => {
        if (!window.confirm(t("recipes.delete_confirm"))) return;
        try {
            await deleteRecipe(r.id);
            setRecipes((list) => list.filter((x) => x.id !== r.id));
            toast.success(t("recipes.deleted"));
        } catch {
            toast.error("Delete failed");
        }
    };

    const setStars = async (r, stars) => {
        // toggle off if clicking same star
        const isClear = r.my_rating === stars;
        try {
            const updated = isClear ? await clearRecipeRating(r.id) : await rateRecipe(r.id, stars);
            setRecipes((list) => list.map((x) => (x.id === r.id ? updated : x)));
        } catch {
            toast.error("Rate failed");
        }
    };

    return (
        <div className="min-h-screen flex flex-col" data-testid="recipes-page">
            <header className="sticky top-0 z-30 bg-[#FBF6EB] border-b-2 border-[#0D2A1B]">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            data-testid="recipes-back-btn"
                            onClick={() => nav(-1)}
                            className="brut-border rounded-lg bg-white w-10 h-10 flex items-center justify-center hover:bg-[#EFEAD8]"
                            aria-label="back"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#2F6B43]">{t("recipes.eyebrow")}</div>
                            <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-none">{t("recipes.title")}</div>
                        </div>
                    </div>
                    {editing !== "new" && (
                        <button
                            data-testid="recipes-add-btn"
                            onClick={startNew}
                            className="brut-btn text-xs sm:text-sm whitespace-nowrap"
                        >
                            <Plus className="w-4 h-4" /> {t("recipes.add")}
                        </button>
                    )}
                </div>
            </header>

            <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-6 space-y-4">
                <div className="relative">
                    <Search className="w-4 h-4 text-[#5b6d63] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        data-testid="recipes-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("recipes.search_placeholder")}
                        className="brut-input !pl-9 !py-2.5 text-sm w-full"
                    />
                </div>

                {editing === "new" && (
                    <RecipeForm
                        draft={draft}
                        setDraft={setDraft}
                        onSave={save}
                        onCancel={cancelEdit}
                        t={t}
                        testIdPrefix="new"
                    />
                )}

                {busy && recipes.length === 0 ? (
                    <div className="brut-card p-6 text-center text-sm text-zinc-500">Loading…</div>
                ) : !filtered.length ? (
                    <div className="brut-card p-6 text-center text-sm text-zinc-500">{t("recipes.empty")}</div>
                ) : (
                    <ul className="space-y-3">
                        <AnimatePresence>
                            {filtered.map((r) => (
                                <motion.li
                                    key={r.id}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    className="brut-card p-4"
                                    data-testid={`recipe-card-${r.id}`}
                                >
                                    {editing === r.id ? (
                                        <RecipeForm
                                            draft={draft}
                                            setDraft={setDraft}
                                            onSave={save}
                                            onCancel={cancelEdit}
                                            onDelete={() => remove(r)}
                                            t={t}
                                            testIdPrefix={r.id}
                                        />
                                    ) : (
                                        <RecipeCard r={r} onEdit={() => startEdit(r)} onStars={setStars} t={t} />
                                    )}
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </ul>
                )}
            </main>
        </div>
    );
}

function RecipeCard({ r, onEdit, onStars, t }) {
    const ingCount = (r.ingredients || []).length;
    return (
        <div>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display text-xl font-bold tracking-tight truncate">{r.name}</div>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#5b6d63] mt-0.5">
                        {r.servings && (
                            <span className="font-semibold">
                                {r.servings} {t("recipes.servings_unit")}
                            </span>
                        )}
                        {ingCount > 0 && (
                            <span data-testid={`recipe-ing-count-${r.id}`}>
                                · {ingCount === 1
                                    ? t("week.ingredients_count_one", { n: ingCount })
                                    : t("week.ingredients_count_other", { n: ingCount })}
                            </span>
                        )}
                    </div>
                    {r.url && (
                        <a
                            data-testid={`recipe-url-${r.id}`}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1 text-[12px] font-semibold text-[#2F6B43] hover:underline truncate"
                        >
                            <OpenNewWindow className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{r.url}</span>
                        </a>
                    )}
                </div>
                <button
                    data-testid={`recipe-edit-${r.id}`}
                    onClick={onEdit}
                    className="w-8 h-8 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center shrink-0"
                    aria-label="edit"
                >
                    <EditPencil className="w-3.5 h-3.5" />
                </button>
            </div>
            {r.notes && (
                <div className="mt-2 text-sm text-[#0d2a1b] whitespace-pre-wrap">{r.notes}</div>
            )}
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <Stars value={r.my_rating || 0} onChange={(s) => onStars(r, s)} testIdPrefix={`recipe-rate-${r.id}`} />
                <div className="text-[11px] text-[#5b6d63] tabular-nums">
                    {r.ratings_count > 0
                        ? <><b className="text-[#0d2a1b]">★ {r.avg_rating?.toFixed(1)}</b> · {r.ratings_count} {r.ratings_count === 1 ? t("recipes.ratings_count_one").replace("{n} ", "") : t("recipes.ratings_count_other").replace("{n} ", "")}</>
                        : t("recipes.no_ratings")}
                </div>
            </div>
        </div>
    );
}

function Stars({ value, onChange, testIdPrefix }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => {
                const active = n <= value;
                return (
                    <button
                        key={n}
                        type="button"
                        data-testid={`${testIdPrefix}-${n}`}
                        onClick={() => onChange(n)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-lg leading-none transition-colors ${active ? "text-[#F3D469]" : "text-[#0d2a1b]/15 hover:text-[#F3D469]/60"}`}
                        aria-label={`${n} star`}
                    >
                        ★
                    </button>
                );
            })}
        </div>
    );
}

const UNITS = ["g", "kg", "ml", "L", "pcs", "tbsp", "tsp", "cup", "can", "bag", "other"];
const ING_CATEGORIES = ["Produce", "Dairy", "Meat", "Pantry", "Frozen", "Bakery", "Beverages", "Other"];

function RecipeForm({ draft, setDraft, onSave, onCancel, onDelete, t, testIdPrefix }) {
    const ingredients = draft.ingredients || [];
    const updateIng = (i, patch) => {
        setDraft((d) => ({
            ...d,
            ingredients: (d.ingredients || []).map((ing, idx) => (idx === i ? { ...ing, ...patch } : ing)),
        }));
    };
    const addIng = () => {
        setDraft((d) => ({
            ...d,
            ingredients: [...(d.ingredients || []), { name: "", qty: "", unit: "", category: "" }],
        }));
    };
    const removeIng = (i) => {
        setDraft((d) => ({
            ...d,
            ingredients: (d.ingredients || []).filter((_, idx) => idx !== i),
        }));
    };

    return (
        <div className="space-y-2">
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">{t("recipes.name")}</div>
                <input
                    data-testid={`recipe-form-name-${testIdPrefix}`}
                    autoFocus
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder={t("recipes.name_placeholder")}
                    className="brut-input !py-2 !text-sm w-full"
                />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">{t("recipes.url")}</div>
                <input
                    data-testid={`recipe-form-url-${testIdPrefix}`}
                    type="url"
                    value={draft.url}
                    onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                    placeholder={t("recipes.url_placeholder")}
                    className="brut-input !py-2 !text-sm w-full"
                />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">
                    {t("recipes.servings_label")}
                </div>
                <div className="flex items-center gap-2">
                    <input
                        data-testid={`recipe-form-servings-${testIdPrefix}`}
                        type="number"
                        min="1"
                        max="50"
                        value={draft.servings ?? 4}
                        onChange={(e) => setDraft((d) => ({ ...d, servings: parseInt(e.target.value, 10) || 1 }))}
                        className="brut-input !py-2 !text-sm w-20 text-center font-bold"
                    />
                    <span className="text-xs text-[#5b6d63]">{t("recipes.servings_unit")}</span>
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">{t("recipes.notes")}</div>
                <textarea
                    data-testid={`recipe-form-notes-${testIdPrefix}`}
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    placeholder={t("recipes.notes_placeholder")}
                    rows={3}
                    className="brut-input !py-2 !text-sm w-full"
                />
            </div>

            {/* Ingredients editor */}
            <div className="pt-2 border-t border-[#0d2a1b]/10">
                <datalist id={`unit-list-${testIdPrefix}`}>
                    {UNITS.filter((u) => u !== "other").map((u) => (
                        <option key={u} value={u}>{t(`recipes.unit.${u}`)}</option>
                    ))}
                </datalist>
                <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63]">
                        {t("recipes.ingredients_title")}
                    </div>
                    <button
                        type="button"
                        onClick={addIng}
                        className="text-[11px] font-semibold text-[#2F6B43] hover:underline"
                        data-testid={`recipe-form-add-ing-${testIdPrefix}`}
                    >
                        {t("recipes.add_ingredient")}
                    </button>
                </div>
                {ingredients.length === 0 && (
                    <div className="text-[11px] text-[#5b6d63] italic mb-1">{t("recipes.ingredients_help")}</div>
                )}
                <ul className="space-y-1.5">
                    {ingredients.map((ing, i) => (
                        <li key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`recipe-form-ing-row-${testIdPrefix}-${i}`}>
                            <input
                                value={ing.name || ""}
                                onChange={(e) => updateIng(i, { name: e.target.value })}
                                placeholder={t("recipes.ingredient_name")}
                                className="brut-input !py-1.5 !text-sm col-span-5"
                                data-testid={`recipe-form-ing-name-${testIdPrefix}-${i}`}
                            />
                            <input
                                type="number"
                                step="any"
                                min="0"
                                value={ing.qty === null || ing.qty === undefined ? "" : ing.qty}
                                onChange={(e) => updateIng(i, { qty: e.target.value })}
                                placeholder="0"
                                className="brut-input !py-1.5 !text-sm col-span-2 text-center"
                                data-testid={`recipe-form-ing-qty-${testIdPrefix}-${i}`}
                            />
                            <input
                                list={`unit-list-${testIdPrefix}`}
                                value={ing.unit || ""}
                                onChange={(e) => updateIng(i, { unit: e.target.value })}
                                placeholder={t("recipes.ingredient_unit")}
                                className="brut-input !py-1.5 !text-sm col-span-2"
                                data-testid={`recipe-form-ing-unit-${testIdPrefix}-${i}`}
                            />
                            <select
                                value={ing.category || ""}
                                onChange={(e) => updateIng(i, { category: e.target.value })}
                                className="brut-input !py-1.5 !text-sm col-span-2"
                                data-testid={`recipe-form-ing-cat-${testIdPrefix}-${i}`}
                            >
                                <option value="">–</option>
                                {ING_CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => removeIng(i)}
                                aria-label={t("recipes.ingredient_remove")}
                                className="col-span-1 h-9 rounded-lg text-[#A53F3F] hover:bg-[#fbe0dc] flex items-center justify-center"
                                data-testid={`recipe-form-ing-remove-${testIdPrefix}-${i}`}
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Steps editor */}
            <div className="pt-2 border-t border-[#0d2a1b]/10">
                <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63]">
                        {t("recipes.steps_title")}
                    </div>
                    <button
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, steps: [...(d.steps || []), ""] }))}
                        className="text-[11px] font-semibold text-[#2F6B43] hover:underline"
                        data-testid={`recipe-form-add-step-${testIdPrefix}`}
                    >
                        {t("recipes.add_step")}
                    </button>
                </div>
                {(draft.steps || []).length === 0 && (
                    <div className="text-[11px] text-[#5b6d63] italic mb-1">{t("recipes.steps_help")}</div>
                )}
                <ol className="space-y-1.5 list-decimal pl-5">
                    {(draft.steps || []).map((step, i) => (
                        <li key={i} className="flex items-start gap-1.5" data-testid={`recipe-form-step-row-${testIdPrefix}-${i}`}>
                            <textarea
                                value={step}
                                onChange={(e) => setDraft((d) => ({
                                    ...d,
                                    steps: (d.steps || []).map((s, idx) => (idx === i ? e.target.value : s)),
                                }))}
                                placeholder={t("recipes.step_placeholder")}
                                rows={2}
                                className="brut-input !py-1.5 !text-sm flex-1 min-h-[40px]"
                                data-testid={`recipe-form-step-${testIdPrefix}-${i}`}
                            />
                            <button
                                type="button"
                                onClick={() => setDraft((d) => ({
                                    ...d,
                                    steps: (d.steps || []).filter((_, idx) => idx !== i),
                                }))}
                                aria-label={t("recipes.step_remove")}
                                className="h-9 px-2 rounded-lg text-[#A53F3F] hover:bg-[#fbe0dc] flex items-center justify-center shrink-0"
                                data-testid={`recipe-form-step-remove-${testIdPrefix}-${i}`}
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="flex gap-2 pt-1">
                <button onClick={onSave} className="brut-btn text-xs flex-1 justify-center" data-testid={`recipe-form-save-${testIdPrefix}`}>
                    <Check className="w-3.5 h-3.5" /> Save
                </button>
                <button onClick={onCancel} className="brut-btn brut-btn-secondary text-xs" data-testid={`recipe-form-cancel-${testIdPrefix}`}>
                    <Xmark className="w-3.5 h-3.5" />
                </button>
                {onDelete && (
                    <button
                        onClick={onDelete}
                        className="brut-btn brut-btn-secondary text-xs !text-[#A53F3F] hover:!bg-[#fbe0dc]"
                        data-testid={`recipe-form-delete-${testIdPrefix}`}
                        aria-label="delete recipe"
                    >
                        <Trash className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
